async function wait(duration) {
  return new Promise((resolve) => setTimeout(resolve, duration));
}
async function retryAsync(fn, {
  attempts,
  interval,
  onError,
  operation,
  operationExtra,
  logger
}) {
  let currentTry = 0;
  let latestError;
  while (currentTry < attempts) {
    try {
      currentTry += 1;
      const result = await fn({
        attempt: currentTry,
        isFinalAttempt: currentTry === attempts
      });
      logger.debug(
        {
          operation,
          extra: operationExtra,
          attempt: currentTry,
          maxAttempts: attempts
        },
        `RetryAsync: Completed ${operation} using retryAsync`
      );
      return result;
    } catch (error) {
      onError == null ? void 0 : onError({ error, currentTry });
      latestError = error;
      if (currentTry < attempts) {
        logger.warn(
          {
            operation,
            extra: operationExtra,
            attempt: currentTry,
            maxAttempts: attempts,
            err: error
          },
          `RetryAsync: One of the attempts of ${operation} failed whilst using retryAsync`
        );
        await wait(interval);
      }
    }
  }
  logger.warn(
    {
      operation,
      extra: operationExtra,
      attempt: currentTry,
      maxAttempts: attempts,
      err: latestError
    },
    `RetryAsync: The ${operation} failed all attempts whilst using retryAsync`
  );
  throw latestError || new Error(`Invalid attempts: ${attempts}`);
}
function jitter(value) {
  const jitteredValue = Math.random() * value;
  return Math.round(jitteredValue);
}
async function backOff(callbackFn, {
  attempts = 5,
  delay = 500,
  maxDelay = 1e4,
  withJitter = 0
} = {}, { operation, operationExtra, logger }) {
  try {
    const result = await callbackFn();
    logger.debug(
      {
        operation,
        extra: operationExtra,
        remainingAttempts: attempts,
        delay
      },
      `RetryBackoff: retry with backOff ${operation} complete`
    );
    return result;
  } catch (e) {
    if (attempts > 1) {
      logger.warn(
        {
          operation,
          extra: operationExtra,
          remainingAttempts: attempts,
          error: e
        },
        `RetryBackoff: retry attempt with backOff ${operation} failed`
      );
      await wait(Math.min(delay, maxDelay));
      const nextDelay = delay * 2;
      return await backOff(
        callbackFn,
        {
          attempts: attempts - 1,
          delay: nextDelay + jitter(withJitter),
          maxDelay
        },
        {
          operation,
          operationExtra,
          logger
        }
      );
    }
    logger.warn(
      {
        operation,
        extra: operationExtra,
        remainingAttempts: attempts,
        error: e
      },
      `RetryBackoff: all retries with backOff ${operation} failed`
    );
    throw e;
  }
}

export { backOff, jitter, retryAsync };
